/**
 * Components using the react-intl module require access to the intl context.
 * This is not available when mounting single components in Enzyme.
 * These helper functions aim to address that and wrap a valid,
 * English-locale intl context around them.
 */

import { includes } from 'lodash';
import * as React from 'react';
import { IntlProvider, intlShape, addLocaleData } from 'react-intl';
import { mount, shallow } from 'enzyme';
import * as en from 'react-intl/locale-data/en';
import * as ja from 'react-intl/locale-data/ja';
import * as zh from 'react-intl/locale-data/zh';
import * as id from 'react-intl/locale-data/id';
import antdEn from 'antd/lib/locale-provider/en_US';
import antdZh from 'antd/lib/locale-provider/ja_JP';
import antdJa from 'antd/lib/locale-provider/zh_TW';

import * as localEn from 'constants/translations/frontend/en-US.json';
import * as localJa from 'constants/translations/frontend/ja.json';
import * as localZh from 'constants/translations/frontend/zh-CN.json';
import * as localId from 'constants/translations/frontend/id.json';

const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = [DEFAULT_LOCALE, 'zh', 'ja', 'id'];

export const browserLocale =
  (navigator.languages && navigator.languages[0]) || navigator.language;

/**
 * Define custom formatters here.
 */
export const getFormats = () => ({
  number: {
    usd: { style: 'currency', currency: 'USD' },
  },
});

/**
 * Given browser's locale, returns only locales supported by the app. Defaults to 'en'.
 */
export function getAppLocale(browserLocale: string): string {
  if (browserLocale && includes(SUPPORTED_LOCALES, browserLocale.slice(0, 2))) {
    return browserLocale;
  }
  return DEFAULT_LOCALE;
}

export const getLocale = () => {
  // need for test
  if (!window.hasOwnProperty('localStorage')) {
    return getAppLocale(browserLocale);
  }

  const item = JSON.parse(window.localStorage.getItem('sourcesage') || '{}');
  if (item && item.i18n && item.i18n.locale) {
    return item.i18n.locale;
  }
  return getAppLocale(browserLocale);
};

export function getLocaleMessage(browserLocale: string) {
  // Define user's language. Different browsers have the user locale defined
  addLocale(browserLocale);
  // Split locales with a region code
  const languageWithoutRegionCode = browserLocale
    .toLowerCase()
    .split(/[_-]+/)[0];
  const localMsg = flattenMessages(
    getLocalTranslation(languageWithoutRegionCode)
  );
  const antdMsg = getAntdTranslation(languageWithoutRegionCode);
  return { browserLocale, localMsg, antdMsg };
}
function getAntdTranslation(locale: string) {
  if (locale.startsWith('zh')) {
    let t: typeof antdZh = require('antd/lib/locale-provider/zh_TW');
    return t;
  } else if (locale.startsWith('ja') || locale.startsWith('jp')) {
    let t: typeof antdJa = require('antd/lib/locale-provider/ja_JP');
    return t;
  }
  let t: typeof antdEn = require('antd/lib/locale-provider/en_US');
  return t;
}

function getLocalTranslation(locale: string) {
  if (locale.startsWith('zh')) {
    let t: typeof localZh = require('constants/translations/frontend/zh-CN');
    return t;
  } else if (locale.startsWith('ja')) {
    let t: typeof localJa = require('constants/translations/frontend/ja');
    return t;
  } else if (locale.startsWith('id')) {
    let t: typeof localId = require('constants/translations/frontend/id');
    return t;
  }
  let t: typeof localEn = require('constants/translations/frontend/en-US');
  return t;
}

function addLocale(locale: string) {
  if (locale.startsWith('zh')) {
    addLocaleData([...zh]);
  } else if (locale.startsWith('ja')) {
    addLocaleData([...ja]);
  } else if (locale.startsWith('id')) {
    addLocaleData([...id]);
  } else {
    addLocaleData([...en]);
  }
}

// You can pass your messages to the IntlProvider. Optional: remove if unneeded.
const { localMsg } = getLocaleMessage(DEFAULT_LOCALE);

// Create the IntlProvider to retrieve context for wrapping around.
const intlProvider = new IntlProvider(
  { locale: DEFAULT_LOCALE, messages: localMsg, formats: getFormats() },
  {}
);
const { intl } = intlProvider.getChildContext();

/**
 * When using React-Intl `injectIntl` on components, props.intl is required.
 */
function nodeWithIntlProp(node) {
  return React.cloneElement(node, { intl });
}

/**
 * Export these methods.
 */
export function shallowWithIntl(node) {
  return shallow(nodeWithIntlProp(node), { context: { intl } });
}

export function mountWithIntl(node) {
  return mount(nodeWithIntlProp(node), {
    context: { intl },
    childContextTypes: { intl: intlShape },
  });
}

function flattenMessages(nestedMessages: Object, prefix = '') {
  return Object.keys(nestedMessages).reduce((messages: Object, key: string) => {
    let value = nestedMessages[key];
    let prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      messages[prefixedKey] = value;
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }

    return messages;
  }, {});
}



// WEBPACK FOOTER //
// ./app/utils/intl.ts